import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  DoCheck,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import {
  catchError,
  forkJoin,
  lastValueFrom,
  map,
  Observable,
  throwError,
} from 'rxjs';
interface Budget {
  budgetID: string;
  budgetName: string;
  fiscalYear: number;
  budgetType: string;
  status: string;
  description: string;
  createdAt: Date;
  userIDCreated: string;
  totalActualCostFromInventory?: number;
  budgetCreationMonth?: string;
  lineItems?: BudgetLineItem[];
  inventoryTransactions?: InventoryTransactionDTO[];
}

interface BudgetItemSummary {
  itemID: number;
  itemName: string; // This is the budget item name
  budgetType?: string; // Add budgetType property
  lines: {
    name: string;
    budgetAmount: number;
    itemID?: number | null; // Keep itemID for internal mapping
  }[];
  total: number;
  actualTotalCostFromInventory?: number;
  inventoryTransactions?: InventoryTransactionDTOWithItemName[]; // Use the new interface here
}

interface BudgetLineItem {
  budgetLineID: number;
  budgetID: number;
  fiscalPeriodID: number;
  costCenterID?: number | null;
  itemID?: number | null;
  costCategory: string;
  budgetAmount: number;
  notes?: string;
}

export interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string; // The item name you want to fetch
  categoryID: number;
  description?: string;
  itemTypeID: number;
  purchaseQuantity: number;
  purchaseUOMID: number;
  supplierID: number;
  lowStockThreshold?: number;
  isActive: boolean;
  createdAt?: Date;
  createdBy: string;
  updatedAt?: Date;
  sellingPrice: number;
  profitMarginPercent: number;
  purchasePrice: number;
  valuationMethodCode?: string;
}

interface ActualBudgetCost {
  budgetID: number;
  budgetName: string;
  period: string;
  totalActualCostFromInventory: number;
}

interface SubscriptionDetails {
  id: number;
  tenantId: number;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodStart: string; // Keep as string for initial fetch
  currentPeriodEnd: string;
  planId: string;
  planName: string;
  amount: number;
  currency: string;
  isCancelled: boolean;
  cancelledAt: null;
  createdAt: string;
  updatedAt: string;
  tokenLimit: number; // Add the tokenLimit property
  tenant: null;
}

export interface InventoryTransactionDTO {
  transactionID: number;
  itemID: number;
  warehouseID: number;
  transactionType: string;
  quantityChange: number;
  costPerUnit: number;
  transactionDate: string;
  referenceType: string;
  referenceID: string;
  userID: number;
  notes: string;
  batchNumber: string;
  expiryDate: string;
  referenceTypeCode: string;
}

// NEW INTERFACE: Extends InventoryTransactionDTO to include itemName
export interface InventoryTransactionDTOWithItemName
  extends InventoryTransactionDTO {
  itemName?: string; // Add the item name property
}

@Component({
  selector: 'app-aivor-insights',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './aivor-insights.component.html',
  styleUrls: ['./aivor-insights.component.css'],
})
export class AivorInsightsComponent implements OnInit, OnChanges {
  subscriptionInfo: any = null;
  tenantProfile: any = null;
  actualsNormalized: any[] = [];
  allPeriodsById: Map<string, any> = new Map();
  budgetTotalsMap: BudgetItemSummary[] = [];
  totalTokensConsumed: number | null = null;
  @Input() filterStart: Date | string | null = null;
  @Input() filterEnd: Date | string | null = null;
  @Input() selectedBudgetType: string = 'all';
  // Add new properties for token limit and remaining tokens
  tokenLimit: number = 0;
  tokensRemaining: number | null = null;
  recommendations: string[][] = [];
  suggestedPrice: number = 0;
  markupPrice: number = 0;
  private hasProcessed = false;
  IsLoading = false;
  errorMessage: string | null = null;
  baseColors = [
    '#007A8F',
    '#6C7A3E',
    '#B0213C',
    '#26284F',
    '#7FBF7F',
    '#F3D250',
    '#2C6B47',
    '#A0D83A',
    '#3C9C8E',
    '#FF6F61',
    '#FF2E17',
  ];
  insightIcons = [
    ['bi bi-graph-up-arrow', 'bi bi-clipboard-data'],
    ['bi bi-currency-dollar', 'bi bi-cash-coin'],
    ['bi bi-lightning-charge', 'bi bi-lightbulb'],
    ['bi bi-box-seam', 'bi bi-boxes'],
  ];
  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fetchSubscriptionAndTokens();
  }

  private fetchSubscriptionAndTokens(): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.warn('Auth token not found. Cannot fetch data.');
      return;
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http
      .get<SubscriptionDetails>(
        `${environment.apiUrl}/api/Subscription/GetCurrent`,
        { headers }
      )
      .subscribe({
        next: (subscription) => {
          if (subscription && subscription.currentPeriodStart) {
            // **SECURITY FIX:** Fetch the token limit from the server response
            this.tokenLimit = subscription.tokenLimit;
            const periodStartDate = new Date(
              subscription.currentPeriodStart
            ).toISOString();

            this.http
              .get<number>(
                `${environment.apiUrl}/api/ai/total-tokens-consumed?currentPeriodStart=${periodStartDate}`,
                { headers }
              )
              .subscribe({
                next: (tokens) => {
                  this.totalTokensConsumed = tokens;
                  // **LOGIC FIX:** Use Math.max to prevent the count from going negative
                  this.tokensRemaining = Math.max(
                    0,
                    this.tokenLimit - (this.totalTokensConsumed || 0)
                  );
                  this.cdr.detectChanges();
                },
                error: (error) => {
                  console.error(
                    'Error fetching total tokens for current period:',
                    error
                  );
                  this.totalTokensConsumed = null;
                  this.tokensRemaining = null;
                },
              });
          } else {
            console.error('Subscription details or start date not found.');
            this.totalTokensConsumed = 0;
            this.tokensRemaining = 0;
          }
        },
        error: (error) => {
          console.error('Error fetching subscription details:', error);
          this.totalTokensConsumed = null;
          this.tokensRemaining = null;
        },
      });
  }

  async processAIInsightFlow(): Promise<void> {
    try {
      await this.fetchLatestTenantProfile(); // optional, continue even if it fails
    } catch (err) {
      console.warn('Tenant profile load failed, continuing without it.');
    }
    // **CLIENT-SIDE CHECK:** This is for a better user experience. The server will perform the actual security check.
    if (this.tokensRemaining !== null && this.tokensRemaining <= 0) {
      this.recommendations = [
        [
          'Your monthly token limit has been reached. Please wait for the next billing cycle to use Aivor again.',
        ],
      ];
      this.IsLoading = false;
      this.cdr.detectChanges();
      return;
    }
    await this.fetchBudgetTotalsMap();

    if (
      !Array.isArray(this.budgetTotalsMap) ||
      this.budgetTotalsMap.length === 0
    ) {
      this.IsLoading = false;
      this.recommendations = [
        [
          'There is no Budget Data Availble. Please add some budgets and try again.',
        ],
      ];
      console.warn('No budget data available, skipping AI prompt processing.');
      return;
    }
  }
  getLatestTenantProfileId(): Observable<number | null> {
    const token =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    if (!token) {
      console.error('Authentication error: No auth token found.');
      return throwError(
        () => new Error('Authentication error: No auth token found.')
      );
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const tenantProfilesUrl = `${environment.apiUrl}/api/TenantProfile/GetAll`;

    return this.http.get<any[]>(tenantProfilesUrl, { headers }).pipe(
      map((data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          const latestProfile = data.reduce((prev, current) =>
            prev.tenantProfileID > current.tenantProfileID ? prev : current
          );
          return latestProfile.tenantProfileID;
        } else {
          console.warn('No tenant profiles found or data is empty.');
          return null;
        }
      }),
      catchError((error) => {
        console.error('Error fetching tenant profiles:', error);
        return throwError(() => new Error('No Tenant Profiles.'));
      })
    );
  }
  buttonProcessPrompt() {
    this.recommendations = [];
    this.IsLoading = true;
    this.processAIInsightFlow();
  }
  fetchLatestTenantProfile(): Promise<void> {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found.');
        return reject(new Error('No auth token found.'));
      }

      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });

      this.getLatestTenantProfileId().subscribe({
        next: (latestTenantId) => {
          if (latestTenantId == null) {
            console.warn('No latest TenantProfileId found.');
            return resolve();
          }

          const apiUrl = `${environment.apiUrl}/api/TenantProfile/GetById?TenantProfileId=${latestTenantId}`;
          this.http.get(apiUrl, { headers }).subscribe({
            next: (data) => {
              this.tenantProfile = data;
            },
            error: (error) => {
              console.error('Error fetching tenant profile:', error);
              resolve();
            },
            complete: () => {
              resolve();
            },
          });
        },
        error: (error) => {
          console.error('Error fetching latest tenant profile ID:', error);
          resolve();
        },
      });
    });
  }

  private normalizeBudgetType(type?: string): 'monthly' | 'quarterly' | 'annually' | 'per-item' {
    const t = (type || '')
      .toLowerCase()
      .trim()
      .replace(/[_\s]+/g, '-');
    if (t === 'monthly' || t === 'month') return 'monthly';
    if (t === 'quarterly' || t === 'quarter') return 'quarterly';
    if (t === 'annually' || t === 'annual' || t === 'yearly' || t === 'year') return 'annually';
    if (t === 'per-item' || t === 'peritem' || t === 'per-items') return 'per-item';
    return 'monthly';
  }

  public fetchBudgetTotalsMap(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const token = localStorage.getItem('authToken');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

      try {
        // 1) Fetch budgets, budget lines, items, fiscal periods, and pre-calculated actuals in parallel
        const [
          budgets,
          lineItems,
          items,
          fiscalPeriods,
          actualBudgetCosts,
        ] = await lastValueFrom(
          forkJoin([
            this.http.get<Budget[]>(
              `${environment.apiUrl}/api/Budgets/GetAll`,
              { headers }
            ),
            this.http.get<BudgetLineItem[]>(
              `${environment.apiUrl}/api/BudgetLines/GetAll`,
              { headers }
            ),
            this.http.get<ItemDTO[]>(`${environment.apiUrl}/api/Items/GetAll`, {
              headers,
            }),
            this.http.get<any[]>(`${environment.apiUrl}/api/FiscalPeriods/GetAll`, {
              headers,
            }),
            // Use the new pre-calculated actuals endpoint (singular path)
            this.http.get<ActualBudgetCost[]>(
              `${environment.apiUrl}/api/ActualBudgetCost/GetAll`,
              { headers }
            ),
          ])
        );

        // 2) Build item lookup maps
        const itemNameMap = new Map<number, string>();
        for (const item of items) {
          itemNameMap.set(item.itemID, item.itemName);
        }

        // 3) Group budget lines by budget ID for quick access
        const linesByBudgetID: { [budgetID: number]: BudgetLineItem[] } = {};
        for (const line of lineItems) {
          if (!linesByBudgetID[line.budgetID]) {
            linesByBudgetID[line.budgetID] = [];
          }
          linesByBudgetID[line.budgetID].push(line);
        }

        // 4) Build fiscal period lookup by ID (supports camelCase/PascalCase)
        const allPeriodsById = new Map<string, any>();
        const periodDateRanges = new Map<string, {start: Date, end: Date}>();
        (fiscalPeriods || []).forEach((p: any) => {
          const id = String(p?.fiscalPeriodID ?? p?.FiscalPeriodID ?? '');
          if (id) {
            allPeriodsById.set(id, p);
            const startDate = p?.startDate ?? p?.StartDate;
            const endDate = p?.endDate ?? p?.EndDate ?? startDate;
            if (startDate) {
              periodDateRanges.set(id, {
                start: new Date(startDate),
                end: new Date(endDate)
              });
            }
          }
        });

        // Get date range from inputs
        const rangeStart: Date | null = this.filterStart ? new Date(this.filterStart as any) : null;
        const rangeEnd: Date | null = this.filterEnd ? new Date(this.filterEnd as any) : null;


        // 5) Normalize and dedupe ActualBudgetCost rows by (budgetID, period)
        const actualsByKey = new Map<string, number>();
        for (const row of actualBudgetCosts || []) {
          const bIdStr = String((row as any)?.budgetID ?? (row as any)?.BudgetID ?? '');
          const periodStr = String((row as any)?.period ?? (row as any)?.Period ?? '');
          const amount = Number((row as any)?.totalActualCostFromInventory ?? (row as any)?.TotalActualCostFromInventory ?? 0);

          // Skip if we have a date range and this actual's period is outside it
          if (rangeStart && rangeEnd && periodStr) {
            const periodRange = periodDateRanges.get(periodStr);
            if (!periodRange ||
                periodRange.end < rangeStart ||
                periodRange.start > rangeEnd) {
              continue;
            }
          }

          if (bIdStr && periodStr) {
            const key = `${bIdStr}_${periodStr}`;
            actualsByKey.set(key, (actualsByKey.get(key) || 0) + amount);
          }
        }
        const actualsNormalized = Array.from(actualsByKey.entries()).map(([key, amount]) => {
          const [budgetID, period] = key.split('_');
          return { budgetID, period, totalActualCostFromInventory: amount };
        });

        // 5b) If a date range is provided via inputs, compute allowed fiscal periods for the range
        const periodsForRange = (rangeStart && rangeEnd)
          ? (fiscalPeriods || []).filter((period: any) => {
              const startRaw = period?.startDate ?? period?.StartDate;
              const endRaw = period?.endDate ?? period?.EndDate ?? startRaw;
              if (!startRaw) return false;
              const pStart = new Date(startRaw);
              const pEnd = new Date(endRaw);
              // Overlap check
              return pEnd >= rangeStart && pStart <= rangeEnd;
            })
          : [];

        const allowedIdsForCard = new Set<string>(
          (periodsForRange || []).map((p: any) => String(p?.fiscalPeriodID ?? p?.FiscalPeriodID ?? ''))
        );
        const allowedYearsForCard = new Set<number>(
          (periodsForRange || []).map((p: any) => {
            const y = Number(p?.fiscalYear ?? p?.FiscalYear);
            if (!Number.isNaN(y)) return y;
            const dStr = p?.startDate ?? p?.StartDate;
            const d = dStr ? new Date(dStr) : null;
            return d && !Number.isNaN(d.getTime()) ? d.getFullYear() : (undefined as any);
          }).filter((v: any) => typeof v === 'number')
        );
        const allowedMonthNumsForCard = new Set<number>(
          (periodsForRange || []).map((p: any) => {
            const m = Number(p?.periodNumber ?? p?.PeriodNumber);
            if (!Number.isNaN(m)) return m;
            const dStr = p?.startDate ?? p?.StartDate;
            const d = dStr ? new Date(dStr) : null;
            return d && !Number.isNaN(d.getTime()) ? d.getMonth() + 1 : (undefined as any);
          }).filter((v: any) => typeof v === 'number')
        );

        // 6) Respect the dashboard's selectedBudgetType filter if provided (normalize types)
        const selType = (this.selectedBudgetType && this.selectedBudgetType !== 'all')
          ? this.normalizeBudgetType(this.selectedBudgetType)
          : null;
        const budgetsForInsights = selType
          ? (budgets || []).filter((b: any) => this.normalizeBudgetType(b?.budgetType) === selType)
          : (budgets || []);

        // 7) Build the final budget totals map combining planned and actuals with period-aware aggregation
        const budgetTotalsMap = budgetsForInsights.map((budget) => {
          const bIdNum = Number((budget as any).budgetID);
          const bIdStr = String((budget as any).budgetID);
          const budgetLines = linesByBudgetID[bIdNum] || [];
          const lines = budgetLines.map((line) => ({
            name: itemNameMap.get(line.itemID ?? -1) || 'Unknown Item',
            budgetAmount: line.budgetAmount,
            itemID: line.itemID,
          }));

          const totalPlannedBudget = lines.reduce(
            (sum, line) => sum + line.budgetAmount,
            0
          );

          // Derive budget type and expand line periods to monthly fiscal periods as needed
          const budgetTypeNorm = this.normalizeBudgetType((budget as any)?.budgetType);
          const itemPeriodIds = new Set<string>((budgetLines || []).map((li) => String(li?.fiscalPeriodID)));
          const expandedPeriodIds = new Set<string>();

          // Helper to get year and month for a fiscal period entry
          const getYearAndMonth = (fp: any): { fy?: number; month?: number } => {
            let fy = Number(fp?.fiscalYear ?? fp?.FiscalYear);
            let monthNum = Number(fp?.periodNumber ?? fp?.PeriodNumber);
            if (Number.isNaN(fy) || Number.isNaN(monthNum)) {
              const dStr = fp?.startDate ?? fp?.StartDate;
              if (dStr) {
                const d = new Date(dStr);
                if (!Number.isNaN(d.getTime())) {
                  if (Number.isNaN(fy)) fy = d.getFullYear();
                  if (Number.isNaN(monthNum)) monthNum = d.getMonth() + 1;
                }
              }
            }
            return { fy: Number.isNaN(fy) ? undefined : fy, month: Number.isNaN(monthNum) ? undefined : monthNum };
          };

          // Expand based on budget type
          for (const id of itemPeriodIds) {
            const fp = allPeriodsById.get(id);
            if (!fp) continue;
            const { fy, month } = getYearAndMonth(fp);
            const fpId = String(fp?.fiscalPeriodID ?? fp?.FiscalPeriodID ?? id);

            if (budgetTypeNorm === 'quarterly') {
              if (fy != null && month != null) {
                const quarter = Math.floor((month - 1) / 3) + 1;
                const startM = 3 * (quarter - 1) + 1;
                const endM = 3 * quarter;
                for (const ap of (fiscalPeriods || [])) {
                  const apType = String(ap?.periodType ?? ap?.PeriodType ?? '').toLowerCase();
                  const y = Number(ap?.fiscalYear ?? ap?.FiscalYear);
                  const m = Number(ap?.periodNumber ?? ap?.PeriodNumber);
                  const apId = String(ap?.fiscalPeriodID ?? ap?.FiscalPeriodID ?? '');

                  // Check if this period falls within our date range
                  if (rangeStart && rangeEnd) {
                    const periodRange = periodDateRanges.get(apId);
                    if (!periodRange ||
                        periodRange.end < rangeStart ||
                        periodRange.start > rangeEnd) {
                      continue;
                    }
                  }

                  if (apType.includes('month') && y === fy && m >= startM && m <= endM && apId) {
                    expandedPeriodIds.add(apId);
                  }
                }
              } else {
                // Check if this period falls within our date range
                if (rangeStart && rangeEnd) {
                  const periodRange = periodDateRanges.get(fpId);
                  if (periodRange &&
                      periodRange.end >= rangeStart &&
                      periodRange.start <= rangeEnd) {
                    expandedPeriodIds.add(fpId);
                  }
                } else {
                  expandedPeriodIds.add(fpId);
                }
              }
            } else if (budgetTypeNorm === 'annually') {
              // For annual budgets, include ALL monthly periods in the fiscal year that overlap with date range
              // Don't restrict by the original budget line period - that's just where it was created
              const budgetFiscalYear = fy || new Date().getFullYear(); // Use budget's fiscal year or current year

              for (const ap of (fiscalPeriods || [])) {
                const apType = String(ap?.periodType ?? ap?.PeriodType ?? '').toLowerCase();
                const y = Number(ap?.fiscalYear ?? ap?.FiscalYear);
                const apId = String(ap?.fiscalPeriodID ?? ap?.FiscalPeriodID ?? '');

                // For annual budgets, include all months in the same fiscal year
                if (apType.includes('month') && y === budgetFiscalYear && apId) {
                  // Check if this period falls within our date range
                  if (rangeStart && rangeEnd) {
                    const periodRange = periodDateRanges.get(apId);
                    if (periodRange &&
                        periodRange.end >= rangeStart &&
                        periodRange.start <= rangeEnd) {
                      expandedPeriodIds.add(apId);
                    }
                  } else {
                    expandedPeriodIds.add(apId);
                  }
                }
              }
            } else {
              // monthly / per-item: keep the specific month id
              // Check if this period falls within our date range
              if (rangeStart && rangeEnd) {
                const periodRange = periodDateRanges.get(fpId);
                if (periodRange &&
                    periodRange.end >= rangeStart &&
                    periodRange.start <= rangeEnd) {
                  expandedPeriodIds.add(fpId);
                }
              } else {
                expandedPeriodIds.add(fpId);
              }
            }
          }

          // Prepare helpers for period matching
          const allowedIdsForBudget = new Set<string>(expandedPeriodIds);
          const periodNumberToAllowedIds = new Map<number, string[]>();
          const yearsForAllowedIds = new Set<number>();
          for (const id of allowedIdsForBudget) {
            const fp = allPeriodsById.get(id);
            const pNum = Number(fp?.periodNumber ?? fp?.PeriodNumber);
            const y = Number(fp?.fiscalYear ?? fp?.FiscalYear);
            if (!Number.isNaN(pNum)) {
              const arr = periodNumberToAllowedIds.get(pNum) || [];
              arr.push(id);
              periodNumberToAllowedIds.set(pNum, arr);
            }
            if (!Number.isNaN(y)) yearsForAllowedIds.add(y);
          }

          // For quarterly/annual budgets, aggregate all actuals in the period (ignore budgetID match)
          const matchByBudgetId = (budgetTypeNorm === 'monthly' || budgetTypeNorm === 'per-item');
          let actualTotal = 0;

          for (const entry of actualsNormalized) {
            const periodStr = String(entry.period);
            const amt = Number(entry.totalActualCostFromInventory || 0);
            let include = false;

            // For monthly/per-item budgets: match by budgetID and period
            if (matchByBudgetId) {
              if (entry.budgetID !== bIdStr) continue;

              // 1) Exact fiscalPeriodID match within expanded months
              if (allowedIdsForBudget.has(periodStr)) {
                include = true;
              } else {
                // 2) Fallback by number interpretation: month number or year number
                const pNum = Number(periodStr);
                if (!Number.isNaN(pNum)) {
                  // Looks like a year: include if allowed years contain it
                  if (periodStr.length === 4 && pNum >= 1900 && pNum <= 3000) {
                    if (yearsForAllowedIds.has(pNum)) include = true;
                  } else if (pNum >= 1 && pNum <= 12) {
                    // Month-number-only; include if month exists in allowed set and allowedYears is unambiguous
                    if (periodNumberToAllowedIds.has(pNum) && yearsForAllowedIds.size <= 1) include = true;
                  }
                }
              }
            } else {
              // For quarterly/annual budgets: aggregate all actuals from constituent periods (ignore budgetID)
              // 1) Exact fiscalPeriodID match within expanded months
              if (allowedIdsForBudget.has(periodStr)) {
                include = true;
              } else {
                // 2) Fallback by number interpretation: month number or year number
                const pNum = Number(periodStr);
                if (!Number.isNaN(pNum)) {
                  // Looks like a year: include if allowed years contain it
                  if (periodStr.length === 4 && pNum >= 1900 && pNum <= 3000) {
                    if (yearsForAllowedIds.has(pNum)) include = true;
                  } else if (pNum >= 1 && pNum <= 12) {
                    // Month-number-only; include if month exists in allowed set and allowedYears is unambiguous
                    if (periodNumberToAllowedIds.has(pNum) && yearsForAllowedIds.size <= 1) include = true;
                  }
                }
              }
            }

            // If a date range filter is provided by the dashboard, also require membership in that range
            if (include && rangeStart && rangeEnd) {
              let includeByCard = false;
              if (allowedIdsForCard.has(periodStr)) {
                includeByCard = true;
              } else {
                const pNum = Number(periodStr);
                if (!Number.isNaN(pNum)) {
                  if (periodStr.length === 4 && pNum >= 1900 && pNum <= 3000) {
                    includeByCard = allowedYearsForCard.has(pNum);
                  } else if (pNum >= 1 && pNum <= 12) {
                    includeByCard = allowedMonthNumsForCard.has(pNum) && allowedYearsForCard.size <= 1;
                  }
                }
              }
              include = includeByCard;
            }

            if (include) actualTotal += amt;
          }

          const budgetSummary: BudgetItemSummary = {
            itemID: bIdNum,
            itemName: budget.budgetName,
            lines,
            total: totalPlannedBudget,
            actualTotalCostFromInventory: actualTotal,
            inventoryTransactions: [],
          };

          return budgetSummary;
        });

        this.budgetTotalsMap = budgetTotalsMap;
        resolve();
      } catch (error) {
        console.error(
          'Error fetching budget data using period-aware ActualBudgetCost aggregation:',
          error
        );
        reject(error);
      }
    });
  }

  getInsightIconColor(index: number): string {
    const colorIndices = [6, 0, 5, 9];
    const colorIndex = index % colorIndices.length;
    return this.baseColors[colorIndices[colorIndex]];
  }

  getInsightIcon(recommendationIndex: number, sentenceIndex: number): string {
    const iconSetIndex = recommendationIndex % this.insightIcons.length;
    const iconIndex = sentenceIndex === 0 ? 0 : 1;
    return this.insightIcons[iconSetIndex][iconIndex];
  }

  async handleProcessPrompt(): Promise<void> {
    // Correctly set loading to true at the very beginning
    this.IsLoading = true;
    this.recommendations = []; // Clear old recommendations
    this.errorMessage = null;
    this.cdr.detectChanges();
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // This client-side check is good for UX
      if (this.tokensRemaining !== null && this.tokensRemaining <= 0) {
        this.recommendations = [
          [
            'Your monthly token limit has been reached. Please wait for the next billing cycle to use Aivor again.',
          ],
        ];
        return;
      }

      // Ensure we have the latest budgets and line items before analysis
      await this.fetchBudgetTotalsMap();

      if (
        !Array.isArray(this.budgetTotalsMap) ||
        this.budgetTotalsMap.length === 0
      ) {
        this.recommendations = [
          ['We encountered an issue: There is no Budget Data Available'],
          ['Please add some budgets and try again.'],
        ];
        throw new Error('No budget data available');
      }
      const sanitizedBudgets = this.budgetTotalsMap.map((item) => {
        const plannedAmount = item.total || 0;
        const actualAmount = item.actualTotalCostFromInventory || 0;
        const variance = plannedAmount - actualAmount;
        const variancePercentage = plannedAmount > 0 ? (variance / plannedAmount) * 100 : 0;

        // Get detailed actual cost breakdown for this budget
        const budgetId = String(item.itemID);
        const relatedActuals = (this.actualsNormalized || []).filter((actual: any) =>
          String(actual.budgetID) === budgetId
        );

        const actualBreakdown = relatedActuals.map((actual: any) => {
          const period = this.allPeriodsById.get(String(actual.period));
          return {
            period: actual.period,
            periodName: period ? `${period.fiscalYear || period.FiscalYear}-${period.periodNumber || period.PeriodNumber}` : actual.period,
            periodType: period?.periodType || period?.PeriodType || 'Unknown',
            actualCost: actual.totalActualCostFromInventory || 0,
            startDate: period?.startDate || period?.StartDate || null,
            endDate: period?.endDate || period?.EndDate || null
          };
        });

        return {
          budgetID: item.itemID,
          budgetName: item.itemName || 'Unnamed Item',
          budgetType: item.budgetType || 'Unknown',
          budgetYear: new Date().getFullYear(),
          plannedAmount: plannedAmount,
          actualAmount: actualAmount,
          variance: variance,
          variancePercentage: Math.round(variancePercentage * 100) / 100,
          varianceStatus: variance > 0 ? 'Under Budget' : variance < 0 ? 'Over Budget' : 'On Budget',
          actualBreakdown: actualBreakdown,
          totalActualEntries: actualBreakdown.length,
          averageCostPerEntry: actualBreakdown.length > 0 ? actualAmount / actualBreakdown.length : 0,
          components: (item.lines || []).map((line: any) => ({
            name: line.name || 'Unnamed Component',
            budgetAmount: line.budgetAmount || 0,
            costCenter: line.costCenter || null,
            itemID: line.itemID || null,
            costCategory: line.costCategory || 'Uncategorized'
          })),
        };
      });

      const requestBody = {
        businessDetails: {
          name: this.tenantProfile?.tenantProfileName?.trim() || '',
          industry: this.tenantProfile?.industry?.trim() || '',
          businessModel: this.tenantProfile?.businessModels?.trim() || '',
          location: this.tenantProfile?.location?.trim() || '',
          businessType: this.tenantProfile?.businessType?.trim() || '',
          description: this.tenantProfile?.businessBrief?.trim() || '',
          size: this.tenantProfile?.businessSize?.trim() || '',
          targetMarket: this.tenantProfile?.targetMarkets?.trim() || '',
          vendorType: this.tenantProfile?.vendorType?.trim() || '',
          salesChannels: this.tenantProfile?.salesChannels?.trim() || '',
          keyChallenges: this.tenantProfile?.keyChallenges?.trim() || '',
        },
        budget: { budgetItems: sanitizedBudgets },
        dashboard: { dashboardName: 'costingDashboard' },
        context: {
          dataSource: 'ActualBudgetCost API (pre-calculated actuals)',
          instructions: 'Do not reference inventory transactions; actuals are already aggregated by backend by period.Annual/quarterly budgets aggregate using expanded monthly fiscal periods and intersect with the current date range if provided.',
          filters: {
            selectedBudgetType: this.selectedBudgetType || 'all',
            dateRange: {
              start: this.filterStart || null,
              end: this.filterEnd || null,
            },
          },
        },
      };
      const apiUrl = `${environment.apiUrl}/api/ai/process-prompt`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        if (response.status === 403) {
          const errorBody = await response.json();
          if (
            errorBody.message &&
            errorBody.message.includes('Token limit reached')
          ) {
            this.recommendations = [
              [
                'Your monthly token limit has been reached. Please wait for the next billing cycle to use Aivor again.',
              ],
            ];
            return;
          }
        }
        const errorText = await response
          .text()
          .catch(() => 'Failed to read error response');
        console.error('API Error Response:', errorText);
        let serverMessage: string | null = null;
        try {
          serverMessage = JSON.parse(errorText)?.message || null;
        } catch {
          // Not JSON; fall back to the generic message below.
        }
        throw new Error(
          serverMessage || `Server error: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json().catch((err) => {
        console.error('Failed to parse JSON response:', err);
        throw new Error('Invalid JSON response from server');
      });
      if (!responseData || typeof responseData !== 'object') {
        throw new Error('Invalid response format: expected an object');
      }
      if (!Array.isArray(responseData.recommendations)) {
        console.error(
          'Invalid recommendations data:',
          responseData.recommendations
        );
        throw new Error('Invalid response: recommendations is not an array');
      }

      this.recommendations = responseData.recommendations
        .filter((rec: any) => typeof rec === 'string')
        .map((rec: any) => rec.split(/(?<=\.|\?)\s+/));
    } catch (error: any) {
      console.error('Error in handleProcessPrompt:', error);
      // Clear stale recommendations so a failed request doesn't look like a repeated answer.
      this.recommendations = [];
      this.errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
    } finally {
      // Correctly set IsLoading to false after all async operations are done
      this.IsLoading = false;
      this.fetchSubscriptionAndTokens();
      this.cdr.detectChanges();
    }
  }

  retry(): void {
    this.handleProcessPrompt();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recompute whenever filters change
    if (changes['filterStart'] || changes['filterEnd'] || changes['selectedBudgetType']) {
      // Avoid blocking UI; fire and forget
      this.fetchBudgetTotalsMap().catch(err => {
        console.error('Insights: failed to refresh on input changes', err);
      });
    }
  }
}
